import { LABELS } from '@constants'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Box,
  Typography,
  TextField,
  InputAdornment,
} from '@mui/material'
import { Search } from '@mui/icons-material'
import { ISingleProduct } from '@typings'
import { useState, useEffect } from 'react'
import { ProductApi } from 'src/api/product.api'
import { toast } from 'react-toastify'

interface ProductPickerModalProps {
  open: boolean
  onClose: () => void
  onSelect: (product: ISingleProduct) => void
}

export const ProductPickerModal = ({ open, onClose, onSelect }: ProductPickerModalProps) => {
  const productApi = new ProductApi()
  const [products, setProducts] = useState<ISingleProduct[]>([])
  const [filteredProducts, setFilteredProducts] = useState<ISingleProduct[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')

  useEffect(() => {
    if (open) {
      loadProducts()
    }
  }, [open])

  useEffect(() => {
    if (searchTerm) {
      const filtered = products.filter(
        product =>
          product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          product.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          product.sku?.toLowerCase().includes(searchTerm.toLowerCase())
      )
      setFilteredProducts(filtered)
    } else {
      setFilteredProducts(products)
    }
  }, [searchTerm, products])

  const loadProducts = async () => {
    try {
      setLoading(true)
      const data = await productApi.getAll()
      setProducts(data.filter(p => p.is_active))
      setFilteredProducts(data.filter(p => p.is_active))
    } catch (error) {
      console.error('Failed to load products:', error)
      toast.error('Fout bij het laden van producten')
    } finally {
      setLoading(false)
    }
  }

  const handleSelect = (product: ISingleProduct) => {
    onSelect(product)
    onClose()
    setSearchTerm('')
  }

  const handleClose = () => {
    onClose()
    setSearchTerm('')
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth>
      <DialogTitle>Kies een product uit de catalogus</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2, mt: 1 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Zoek op naam, omschrijving of artikelnummer..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {loading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 200,
            }}>
            <CircularProgress />
          </Box>
        ) : filteredProducts.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography
              variant="body2"
              color="text.secondary">
              {searchTerm
                ? 'Geen producten gevonden die overeenkomen met je zoekopdracht.'
                : 'Nog geen producten beschikbaar. Voeg producten toe in de instellingen.'}
            </Typography>
          </Paper>
        ) : (
          <TableContainer
            component={Paper}
            sx={{ maxHeight: 400 }}>
            <Table
              size="small"
              stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>{LABELS.TITLE}</TableCell>
                  <TableCell>{LABELS.EXTRA_DESCRIPTION}</TableCell>
                  <TableCell align="right">{LABELS.UNIT_PRICE}</TableCell>
                  <TableCell>{LABELS.UNIT}</TableCell>
                  <TableCell align="right">Actie</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredProducts.map(product => (
                  <TableRow
                    key={product.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleSelect(product)}>
                    <TableCell>{product.name}</TableCell>
                    <TableCell>
                      {product.description ? (
                        <Typography
                          variant="body2"
                          sx={{
                            maxWidth: 200,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}>
                          {product.description}
                        </Typography>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell align="right">{product.formatted_price || '-'}</TableCell>
                    <TableCell>{product.unit || '-'}</TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={e => {
                          e.stopPropagation()
                          handleSelect(product)
                        }}>
                        Selecteer
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{LABELS.CANCEL}</Button>
      </DialogActions>
    </Dialog>
  )
}
